// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  dateOnly,
  formatDate,
  type Shipment,
  type ShipmentId,
  type UpdateShipmentBody,
  useEvolveGet,
  useEvolvePatch,
} from '@evolvemep/api-ts';
import { Anchor, Flex, LoadingOverlay, Stack, Text } from '@mantine/core';
import { useParams } from 'react-router';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { CustomPropertiesDetailSection } from 'components/Mantine/CustomProperties/CustomPropertiesDetailSection';
import { DatePickerWrapped } from 'components/Mantine/DateInputWrapped';
import { DescriptionEditor } from 'components/Mantine/DetailPage/DescriptionEditor';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { SmallDetailItem } from 'components/Mantine/DetailPage/SmallDetailItem';
import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { TextareaWrapped, TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import { UserInfoBadge } from 'components/Mantine/UserInfoBadge';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { EditableShipmentStatusBadge } from 'modules/Shop/Shipments/ShipmentDetail/EditableShipmentStatusBadge';
import { ShipmentActivityFeed } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentActivityFeed';
import { ShipmentWorkflowButtons } from 'modules/Shop/Shipments/ShipmentDetail/ShipmentWorkflowButtons';

import { ShipmentItemsList } from './ShipmentItemsList/ShipmentItemsList';
import { useCanEditShipment } from './useCanEditShipment';

const looksLikeUrl = (value: string | null | undefined): value is string =>
  isNotNil(value) && /^https?:\/\//i.test(value);

type Props = {
  shipment: Shipment;
  onShipmentUpdated: (shipment: Shipment) => void;
};

const ShipmentDetailPageWrapped = ({ shipment, onShipmentUpdated }: Props) => {
  const navigate = useEvolveNavigate('Shipping Orders');
  const { canEditShipment } = useCanEditShipment(shipment);

  const { apiCall: patchShipmentApiCall, loading: saving } = useEvolvePatch(`shop/shipment/${shipment.shipmentId}`);
  const updateShipment = useCallback(
    async (body: UpdateShipmentBody) => patchShipmentApiCall(body).then(onShipmentUpdated),
    [patchShipmentApiCall, onShipmentUpdated],
  );

  const { apiCall: getShipmentApiCall } = useEvolveGet(`shop/shipment/${shipment.shipmentId}`);
  const refreshShipment = useCallback(() => {
    void getShipmentApiCall().then(onShipmentUpdated);
  }, [getShipmentApiCall, onShipmentUpdated]);

  const canEdit = (...params: Parameters<typeof canEditShipment>) => canEditShipment(...params) && !saving;

  return (
    <Stack flex={1} style={{ overflow: 'hidden' }} gap={0}>
      <BasePageHeader
        title={shipment.identifier}
        parentPage={{ title: 'Shipping Orders', url: '/shipping-orders' }}
        topRightComponent={
          <ShipmentWorkflowButtons
            shipment={shipment}
            onShipmentUpdated={refreshShipment}
            onShipmentDeleted={() => {
              void navigate('/shipping-orders');
            }}
          />
        }
      />

      <Flex gap="md" flex="1 0 0" pr={8} align="flex-start" style={{ overflowY: 'scroll' }}>
        {/* Left column */}
        <Stack flex={1}>
          <DescriptionEditor
            title={shipment.name}
            description={shipment.description ?? ''}
            updateDescription={(description) => updateShipment({ description })}
            editable={canEdit('editNameDescription')}
            maxLength={1024}
          />

          <ShipmentItemsList shipment={shipment} onItemsUpdated={refreshShipment} />

          <DetailSection title="Activity" large>
            <ShipmentActivityFeed shipment={shipment} />
          </DetailSection>
        </Stack>

        {/* Right sidebar */}
        <Stack miw={300} w={300}>
          <DetailSection title="Status">
            <EditableShipmentStatusBadge
              shipment={shipment}
              editable={canEdit('editStatus')}
              onShipmentUpdated={onShipmentUpdated}
              size="xxl"
              fullWidth
            />
          </DetailSection>

          <DetailSection
            title="Shipping Assignees"
            rightSection={
              <EditableCell
                Editor={MultiUserSelect}
                initialValue={shipment.assignees}
                renderIcon="Add"
                onSave={(assignees) =>
                  updateShipment({ assignees: assignees?.map((assignee) => assignee.userId) ?? [] })
                }
                editable={canEdit('editAssignees')}
              />
            }
          >
            <EditableCell
              Editor={MultiUserSelect}
              initialValue={shipment.assignees}
              renderValue={
                <Stack gap="xs">
                  {shipment.assignees.map((assignee) => (
                    <UserInfoBadge key={assignee.userId} user={assignee} />
                  ))}
                </Stack>
              }
              emptyState={
                <>
                  <EvolveIcon icon="EmptyCircle" size="sm" />
                  Add assignee
                </>
              }
              showEmptyState={shipment.assignees.length === 0}
              onSave={(assignees) => updateShipment({ assignees: assignees?.map((assignee) => assignee.userId) ?? [] })}
              editable={canEdit('editAssignees')}
            />
          </DetailSection>

          <DetailSection title="Shipping Info">
            <EditableCell
              label="Ship To"
              Editor={TextareaWrapped}
              editorProps={{ maxLength: 256 }}
              initialValue={shipment.shipTo}
              onSave={(shipTo) => updateShipment({ shipTo })}
              renderValue={<Text style={{ whiteSpace: 'pre-wrap' }}>{shipment.shipTo}</Text>}
              editable={canEdit('editShippingInfo')}
            />
            <EditableCell
              label="Tracking #"
              Editor={TextInputWrapped}
              editorProps={{ maxLength: 2048 }}
              initialValue={shipment.trackingNumber}
              onSave={(trackingNumber) => updateShipment({ trackingNumber })}
              renderValue={
                looksLikeUrl(shipment.trackingNumber) ? (
                  <Anchor href={shipment.trackingNumber} target="_blank" rel="noopener noreferrer" fz="sm">
                    {shipment.trackingNumber}
                  </Anchor>
                ) : (
                  shipment.trackingNumber
                )
              }
              editable={canEdit('editShippingInfo')}
            />
          </DetailSection>

          <DetailSection title="Dates">
            <EditableCell
              label="Ship By"
              labelTooltip="The requested date that the shipment must be shipped by."
              Editor={DatePickerWrapped}
              initialValue={shipment.shipByDate}
              editorProps={{ minDate: dateOnly(shipment.createdOn) }}
              onSave={(shipByDate) => updateShipment({ shipByDate })}
              renderValue={formatDate(shipment.shipByDate, 'MM/dd/yyyy')}
              closeOnChange
              editable={canEdit('editDates')}
            />
            <EditableCell
              label="Scheduled Ship"
              labelTooltip="The date which has been coordinated as the actual shipment date."
              Editor={DatePickerWrapped}
              initialValue={shipment.scheduledShipDate}
              editorProps={{ minDate: dateOnly(shipment.createdOn) }}
              onSave={(scheduledShipDate) => updateShipment({ scheduledShipDate })}
              renderValue={formatDate(shipment.scheduledShipDate, 'MM/dd/yyyy')}
              closeOnChange
              editable={canEdit('editDates')}
            />
            <SmallDetailItem label="Shipped">{formatDate(shipment.shippedOn, 'MM/dd/yyyy')}</SmallDetailItem>
            <SmallDetailItem label="Delivered">{formatDate(shipment.deliveredOn, 'MM/dd/yyyy')}</SmallDetailItem>
          </DetailSection>

          <CustomPropertiesDetailSection
            entity={shipment}
            disabled={saving}
            customPropertyTypeName="Shipment"
            onUpdateCustomProperty={(customPropertyId, value) =>
              updateShipment({
                customProperties: [{ customPropertyId, value }],
              })
            }
          />

          {shipment.relatedWorkOrders.length > 0 && (
            <DetailSection title="Contains Items From">
              <Stack gap={4}>
                {shipment.relatedWorkOrders.map((workOrder) => (
                  <EvolveLink
                    key={workOrder.workOrderId}
                    to={`/work-orders/${workOrder.workOrderId}`}
                    from={shipment.identifier}
                  >
                    <Flex align="center" gap="xs">
                      <EvolveIcon icon="WorkOrder" light />
                      {workOrder.workOrderName}
                    </Flex>
                  </EvolveLink>
                ))}
              </Stack>
            </DetailSection>
          )}

          <DetailSection title="Properties">
            <SmallDetailItem label="Shipping Order Id">{shipment.identifier}</SmallDetailItem>
            <SmallDetailItem label="Facility">{shipment.facilityName}</SmallDetailItem>
            <SmallDetailItem label="Project">{shipment.projectName}</SmallDetailItem>
            <SmallDetailItem label="Created By">
              <UserInfoBadge user={shipment.createdByUser} />
            </SmallDetailItem>
          </DetailSection>
        </Stack>
      </Flex>
    </Stack>
  );
};

export const ShipmentDetailPage = () => {
  const navigate = useEvolveNavigate('', true);
  const [shipment, setShipment] = useState<Shipment | null>(null);

  const { shipmentId } = useParams() as { shipmentId?: ShipmentId };

  const { apiCall: getShipment, loading: loadingShipment } = useEvolveGet('shop/shipment/:shipmentId');
  const shipmentMatches = useMemo(() => shipment?.shipmentId === shipmentId, [shipment?.shipmentId, shipmentId]);

  useEffect(() => {
    if (isNil(shipmentId)) {
      void navigate('/shipping-orders');
    } else if (!shipmentMatches) {
      getShipment({ url: `shop/shipment/${shipmentId}` })
        .then(setShipment)
        .catch(() => {
          void navigate('/shipping-orders');
        });
    }
  }, [getShipment, navigate, shipmentMatches, shipmentId]);

  if (isNil(shipment) || loadingShipment || !shipmentMatches) {
    return <LoadingOverlay visible />;
  }
  return <ShipmentDetailPageWrapped shipment={shipment} onShipmentUpdated={setShipment} />;
};
